import { BitkitButton, BitkitFormDialog, BitkitLink, BitkitTagsInput, BitkitTextArea } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { Controller, useForm } from 'react-hook-form';

import { QuarantineItem } from '../TestQuarantinePage.types';
import TestQuarantineDialogContent from './TestQuarantineDialogContent';

type TestQuarantineItemDialogProps = {
  isLoading: boolean;
  onCancel: () => void;
  onSubmit: (data: QuarantineItem) => void;
  testQuarantineItem: QuarantineItem;
};

const TestQuarantineItemDialog = (props: TestQuarantineItemDialogProps) => {
  const { isLoading, onCancel, onSubmit, testQuarantineItem } = props;

  const isEdit = Boolean(testQuarantineItem.id);

  const { control, formState, handleSubmit } = useForm<QuarantineItem>({
    defaultValues: {
      ...testQuarantineItem,
      filters: {
        branches: testQuarantineItem.filters?.branches || [],
        targets: testQuarantineItem.filters?.targets || [],
      },
    },
  });

  const isDisabled = Object.keys(formState.dirtyFields).length === 0 && isEdit;

  return (
    <BitkitFormDialog
      state={isLoading ? 'loading' : undefined}
      open
      onOpenChange={({ open }) => !open && onCancel()}
      onSubmit={handleSubmit(onSubmit)}
      title={isEdit ? 'Edit quarantine scope' : 'Set up quarantine'}
      footerButtons={
        <>
          <BitkitButton variant="tertiary" onClick={onCancel}>
            Cancel
          </BitkitButton>
          <BitkitButton state={isLoading ? 'loading' : isDisabled ? 'disabled' : undefined} type="submit">
            {isEdit ? 'Save changes' : 'Quarantine'}
          </BitkitButton>
        </>
      }
    >
      <TestQuarantineDialogContent testQuarantineItem={testQuarantineItem} />
      <Controller
        control={control}
        name="comment"
        render={({ field: { ref: fieldRef, ...fieldRest } }) => (
          <BitkitTextArea ref={fieldRef} optional label="Comment" textareaProps={{ ...fieldRest, maxLength: 500 }} />
        )}
      />
      <Box display="flex" flexDirection="column" gap="16">
        <Box display="flex" flexDirection="column" gap="4">
          <Text as="h3" color="text/primary" textStyle="heading/h3">
            Scope of quarantine
          </Text>
          <Text color="text/secondary" textStyle="body/md/regular">
            Quarantine applies to all branches and targets (Workflows and Pipelines) unless you select specific options.{' '}
            <BitkitLink
              href="https://docs.bitrise.io/en/bitrise-ci/testing/detecting-and-quarantining-flaky-tests.html#defining-the-scope-of-quarantine"
              isExternal
            >
              Learn more about scoping
            </BitkitLink>
          </Text>
        </Box>
        <Controller
          control={control}
          name="filters.branches"
          render={({ field: { onChange, ref: _ref, value } }) => (
            <BitkitTagsInput
              helperText="Enter branches separated by space. Allowed wildcard character: *"
              label="Branches"
              placeholder="Use wildcard pattern (e.g. *main*, release-*)"
              value={value || []}
              onValueChange={({ value: newValues }) => onChange(newValues)}
            />
          )}
        />
        <Controller
          control={control}
          name="filters.targets"
          render={({ field: { onChange, ref: _ref, value } }) => (
            <BitkitTagsInput
              helperText="Enter Workflows and Pipelines separated by space."
              label="Targets"
              value={value || []}
              onValueChange={({ value: newValues }) => onChange(newValues)}
            />
          )}
        />
      </Box>
    </BitkitFormDialog>
  );
};

export default TestQuarantineItemDialog;
